import {
  backOut,
  clampAlpha,
  countUp,
  emphasizedDecelerate,
  linear,
  progressAt,
  shakeOffset,
  slam,
} from 'modules/match-video/domain/scenes/kit';

/** Set B intro length — the same 4 s as set A: 2 s of build-up, 2 s to read. */
export const SLASH_INTRO_SEC = 4;

const PUSH_IN = 0.035;
/** `splitL` / `splitR`: the halves come in from ±1300 px. */
const SPLIT_DISTANCE = 1300;
const COUNT_AT = 1.45;
const COUNT_SEC = 0.65;

const decAt = (t: number, delaySec: number, durationSec: number) => emphasizedDecelerate(progressAt(t, delaySec, durationSec));
const backAt = (t: number, delaySec: number, durationSec: number) => backOut(progressAt(t, delaySec, durationSec));

export interface SlashIntroState {
  pushScale: number;
  shake: { dx: number; dy: number };
  /** Shift of the red half from its place (negative — still to the left); the dark half mirrors it. */
  splitDx: number;
  /** 0…1 — how much of the white slash has grown down from the top (`slashIn`). */
  slashGrow: number;
  /** `drop` of the white top band: shift up, px, and opacity. */
  top: { dy: number; alpha: number };
  champAlpha: number;
  roundAlpha: number;
  homeLogo: { scale: number; alpha: number };
  awayLogo: { scale: number; alpha: number };
  homeName: { dx: number; alpha: number };
  awayName: { dx: number; alpha: number };
  /** Horizontal scale of the score box (`unfold` with the overshooting curve). */
  scoreScaleX: number;
  homeShown: number | null;
  awayShown: number | null;
  foot: { dx: number; alpha: number };
}

const countShown = (score: number | undefined, t: number): number | null =>
  score === undefined ? null : countUp(score, progressAt(t, COUNT_AT, COUNT_SEC));

/**
 * Every animated value of the set B intro at `t` seconds, as the mockup's CSS animations give it (`.b-*` elements,
 * delays and durations from their inline `--d` / `--t`). Pure — tests read it at key moments.
 */
export function slashIntroState(t: number, homeScore?: number, awayScore?: number): SlashIntroState {
  const slide = (sign: 1 | -1, delaySec: number, durationSec: number, distance: number) => {
    const p = decAt(t, delaySec, durationSec);
    // Written so that the rest position is +0, not −0.
    return { dx: sign * distance * (1 - p) + 0, alpha: p };
  };
  const drop = backAt(t, 0.65, 0.4);
  return {
    pushScale: 1 + PUSH_IN * linear(progressAt(t, 0, SLASH_INTRO_SEC)),
    shake: shakeOffset(t, 1.2),
    splitDx: SPLIT_DISTANCE * (decAt(t, 0, 0.55) - 1),
    slashGrow: decAt(t, 0.5, 0.25),
    // `drop`: translate 0 −60px → 0 and opacity 0 → 1 on `--back`; the overshoot of the opacity is clamped.
    top: { dy: 60 * (drop - 1), alpha: clampAlpha(drop) },
    champAlpha: decAt(t, 0.9, 0.5),
    roundAlpha: decAt(t, 0.95, 0.5),
    homeLogo: slam(t, 0.9, 0.45),
    awayLogo: slam(t, 0.95, 0.45),
    homeName: slide(-1, 1.05, 0.5, 300),
    awayName: slide(1, 1.1, 0.5, 300),
    scoreScaleX: backAt(t, 1.35, 0.35),
    homeShown: countShown(homeScore, t),
    awayShown: countShown(awayScore, t),
    foot: slide(-1, 1.9, 0.5, 200),
  };
}
